import { useEffect, useState } from 'react';
import { IconLoader2 } from '@tabler/icons-react';
import { T } from './figma';
import { usePrintEditorStore } from '../state/usePrintEditorStore';
import { ELEMENT_TYPES } from '../schema/documentSchema';
import { urlToImagePayload } from '../utils/imageFiles';

/**
 * "Image from link" — fetches the URL and embeds it as a data URL (see
 * urlToImagePayload for why a raw remote URL would break exporting).
 *
 * Plain overlay rather than the Radix Dialog on purpose: both callers sit inside
 * CSS-transformed containers, and a transformed ancestor makes `position: fixed`
 * resolve against it instead of the viewport. Rendering our own fixed layer keeps
 * it correct from anywhere — including browser fullscreen, where portals to
 * document.body are not painted.
 */
export default function ImageLinkDialog({ open, onClose }) {
    const [url, setUrl] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    // Start clean each time it opens
    useEffect(() => {
        if (open) { setUrl(''); setError(''); setBusy(false); }
    }, [open]);

    if (!open) return null;

    const submit = async () => {
        setBusy(true);
        setError('');
        try {
            const p = await urlToImagePayload(url, { maxSide: 280 });
            usePrintEditorStore.getState().addElements([{
                type: ELEMENT_TYPES.IMAGE, src: p.src, name: p.name, width: p.width, height: p.height,
            }]);
            onClose?.();
        } catch (err) {
            setError(err.message || 'Could not add that image.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-[300] flex items-center justify-center bg-black/50 px-4"
            onPointerDown={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-label="Image from link"
                className={`w-[400px] max-w-full p-4 rounded-[13px] shadow-2xl ${T.panel} ${T.text}
                    border border-[#e6e6e6] dark:border-[#444444]`}
                onPointerDown={(e) => e.stopPropagation()}
            >
                <div className="text-[13px] font-semibold mb-1">Image from link</div>
                <p className={`text-[11px] leading-snug mb-3 ${T.textSoft}`}>
                    The image is embedded into the template, so it keeps working even if the link stops.
                </p>
                <input
                    autoFocus
                    value={url}
                    placeholder="https://example.com/photo.jpg"
                    disabled={busy}
                    onChange={(e) => { setUrl(e.target.value); setError(''); }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && url.trim() && !busy) submit();
                        if (e.key === 'Escape' && !busy) onClose?.();
                        e.stopPropagation(); // keep editor shortcuts out of this field
                    }}
                    className={`w-full h-8 px-2 text-[12px] rounded-[6px] outline-none
                        border border-[#e6e6e6] dark:border-[#444444] ${T.inputBg} ${T.text}
                        focus:ring-1 focus:ring-[#0d99ff]`}
                />
                {error && <p className="mt-2 text-[11px] leading-snug text-[#e5484d]">{error}</p>}
                <div className="mt-3 flex items-center justify-end gap-2">
                    <button
                        type="button"
                        disabled={busy}
                        onClick={() => onClose?.()}
                        className={`h-7 px-3 rounded-[6px] text-[11px] font-medium
                            border border-[#e6e6e6] dark:border-[#444444] ${T.hoverBg} disabled:opacity-40`}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        disabled={busy || !url.trim()}
                        onClick={submit}
                        className="h-7 px-3 rounded-[6px] text-[11px] font-semibold bg-[#0d99ff] text-white
                            hover:bg-[#0b87e0] disabled:opacity-40 flex items-center gap-1.5"
                    >
                        {busy && <IconLoader2 size={12} className="animate-spin" />}
                        {busy ? 'Adding…' : 'Add image'}
                    </button>
                </div>
            </div>
        </div>
    );
}
